import { useEffect, useState } from "react";
import { api, num, formatApiErrorDetail } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Spinner } from "./Spinner";
import { Lock, User, Upload, Clock, FileText } from "lucide-react";

/**
 * FinalizeLogDialog — pulls /entries/by-account/{id}/finalize-log and shows,
 * for the currently-open account, HOW each finalized entry came to be
 * finalized: either a real user hitting the Finalize button (kind="manual",
 * carries the finalizer name + batch time) OR a PDF backfill/import that
 * inserted the row directly as status=finalized (kind="import").
 */
export default function FinalizeLogDialog({ open, onClose, accountId, accountName }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !accountId) return;
    let cancelled = false;
    setBusy(true);
    setErr("");
    setData(null);
    (async () => {
      try {
        const { data } = await api.get(`/entries/by-account/${accountId}/finalize-log`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, accountId]);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="finalize-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Lock size={18} strokeWidth={1.5} className="text-[color:var(--gold-deep)]" />
            Finalize activity — {accountName}
          </DialogTitle>
          <DialogDescription>
            Rows tagged <b>Import</b> were inserted straight as finalized by a
            PDF backfill — nobody clicked Finalize. Rows tagged <b>Manual</b>
            came from a user pressing the Finalize button.
          </DialogDescription>
        </DialogHeader>

        {busy && (
          <div className="py-8 flex items-center justify-center gap-2 text-stone-500">
            <Spinner size={16} /> Loading…
          </div>
        )}
        {err && (
          <div className="rounded-xl px-4 py-3 text-sm bg-red-50/70 text-red-700">
            {err}
          </div>
        )}
        {!busy && !err && data && (
          <div className="space-y-3">
            <div className="text-xs text-stone-500">
              Total finalized entries: <b>{data.total}</b> across {data.groups.length} group(s).
            </div>
            {data.groups.length === 0 && (
              <div className="rounded-xl px-4 py-6 text-center text-sm text-stone-500 bg-stone-50/70">
                No finalized entries on this account.
              </div>
            )}
            <div className="space-y-2 max-h-[54vh] overflow-y-auto">
              {data.groups.map((g, i) => (
                <div
                  key={i}
                  className={`rounded-xl p-3 border ${
                    g.kind === "manual"
                      ? "border-emerald-200 bg-emerald-50/40"
                      : "border-amber-200 bg-amber-50/40"
                  }`}
                  data-testid={`finalize-group-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {g.kind === "manual" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                          <Lock size={10} /> Manual finalize
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                          <Upload size={10} /> Import (inserted as finalized)
                        </span>
                      )}
                      <span className="font-serif text-lg tabular-nums">{g.count}</span>
                      <span className="text-xs text-stone-500">entries</span>
                    </div>
                    <div className="text-xs text-stone-500">
                      <Clock size={10} className="inline mr-1" />
                      {fmt(g.when)}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-col gap-1 text-sm">
                    <div className="flex items-center gap-2">
                      <User size={12} className="text-stone-400" />
                      <span className="text-stone-500 text-xs uppercase tracking-wider">By</span>
                      <span className="font-medium truncate" title={g.who}>{g.who}</span>
                    </div>
                    <div className="text-xs text-stone-500">
                      Entry dates: {fmt(g.first)} → {fmt(g.last)}
                    </div>
                    {g.batch_id && (
                      <div className="text-[10px] text-stone-400 font-mono">
                        batch: {g.batch_id}
                      </div>
                    )}
                    {g.sample_note && (
                      <div className="text-xs text-stone-500 flex items-start gap-1 mt-1">
                        <FileText size={10} className="mt-0.5 shrink-0" />
                        <span className="italic truncate">&ldquo;{g.sample_note}&rdquo;</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
